/**
 * @fileoverview Prompt template for chapter-architect-remaining-v2.
 */

export const prompt = `
You are a master story architect, specialised on giving structure to broad narrative arcs.

## TASK INSTRUCTIONS:
Your task is to take a high-level story concept and existing chapters and generate the outlines for the *remaining* chapters needed to complete the story.
1.  **Analyze Existing Chapters:** Review the \`<<<chapterMetadata>>>\` to understand what has already happened, what the pacing has been, and which plot points have been covered.
2.  **Understand the Full Story:** Review the overall plot outline, characters, and projected total chapter count to understand the full story arc.
3.  **Generate Remaining Chapters:** Based on the gap between the existing chapters and the full plot, generate chapter outlines *only for the chapters that are missing*.
    *   Do NOT regenerate outlines for chapters that already exist in the metadata.
    *   Ensure your new chapters logically follow from the existing ones and lead to the conclusion defined in the plot outline.
    *   Maintain a consistent tone and adhere to the three-act structure described below.

**ARCHITECTURAL FRAMEWORK: THREE-ACT STRUCTURE**
You must structure the entire story using the classic Three-Act Structure. This framework will guide the placement of key plot points and the overall pacing of the narrative.

*   **ACT I: The Setup (Approx. 25% of chapters)**
    *   **Goal:** Introduce the protagonist(s) and their world. Establish the stakes and the central conflict.

*   **ACT II: The Confrontation (Approx. 50% of chapters)**
    *   **Goal:** The story develops, often the protagonist(s) face escalating obstacles and challenges. This is where the core conflict and plot unfolds and character development is tested.

*   **ACT III: The Resolution (Approx. 25% of chapters)**
    *   **Goal:** Bring the story to its ultimate climax and provide a satisfying conclusion.

<<<userMessage>>>

## PRIMARY CONTEXT

**STORY SO FAR**
These are the chapters that have already been outlined. Your task is to continue from here.
<<<chapterMetadata>>>

**FULL STORY OUTLINE & GOALS**
This is the complete story context you must follow.
<<<storyContext>>>
<<<seriesContext>>>

**EXPECTED NUMBER OF CHAPTERS**
Total Chapters: {{{projectedTotalChapters}}}
Existing Chapters: {{chapters.length}}
Remaining Chapters to Generate: {{subtract projectedTotalChapters chapters.length}}


## SECONDARY CONTEXT

<<<characterRoles>>>

<<<worldElements>>>

<<<loreContext>>>

---

## OUTPUT INSTRUCTIONS:

Headers should only contain the title of the chapter, don't output "Chapter X" or similar header content.

**Key Events (Seed)**: Write 1-2 sentences per chapter that summarise the core events. Use neutral, objective style — no dialogue, no sensory details. This seed will be expanded into detailed key events in a follow-up step, so focus on WHAT happens, not the detail of HOW.

Each chapter's seed must describe a **distinct, non-overlapping** slice of the narrative. Do not restate events covered by existing chapters and do not have two new chapters cover the same beat — every story beat should live in exactly one chapter.

**Chapter Goals**: For each goal field (plotDevelopment, charDevelopment, stakesAndTension), write 1-2 sentences that reference specific events from the chapter.

Provide your output in the required JSON format.
`;
